import React, { useState } from 'react'

import CountUp from 'react-countup'

import { apiGenerateReferralCode } from '@src/api/point.api'
import Card from '@src/components/Card'
import CopyToClipboard from '@src/components/CopyToClipboard'
import LoadingIcon from '@src/components/icons/Loading'
import { MIN_MILES_FOR_REFERRAL_CODE } from '@src/constants/miles'
import { useFetchGeneratedReferralCode } from '@src/hooks/useFetchGeneratedReferralCode'
import { ConnectWallet } from '@src/pages/Points/CheckPoints/ConnectWallet'
import PointsBreakdownChart from '@src/pages/Points/CheckPoints/PointsBreakdownChart'
import { shouldDisplayReferralCode } from '@src/utils/miles'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useApplicationStore } from '@src/zustand_stores'

type Props = {
    address: `0x${string}` | undefined
    points: number
    breakdown?: any
}

export const CheckPoints: React.FC<Props> = ({ address, points, breakdown }) => {
    const generatedReferralCode = useApplicationStore(state => state.generatedReferralCode)
    const { refetchGeneratedReferralCode } = useFetchGeneratedReferralCode()
    const [isGenerating, setIsGenerating] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const isBreakdownAnEmptyObject = Object.keys(breakdown || {}).length === 0

    if (!address) return <ConnectWallet />

    const handleGenerateReferralCode = async () => {
        setIsGenerating(true)
        try {
            const result = await apiGenerateReferralCode(address)
            if (result.status !== 'success') throw new Error(result.message)

            refetchGeneratedReferralCode()
        } catch (error) {
            console.error('Error generating referral code', error)
            setError(error instanceof Error ? error.message : 'Something went wrong. Please try again later')
            setIsGenerating(false)
        } finally {
            setIsGenerating(false)
        }
    }

    const renderReferralCode = () => {
        if (shouldDisplayReferralCode(points)) {
            // miles >= 10k and referral code exists
            if (
                generatedReferralCode &&
                generatedReferralCode.code !== '' &&
                generatedReferralCode.userAddress.toLowerCase() === address.toLowerCase()
            ) {
                return (
                    <span className='cursor-pointer text-[#85C3F8]'>
                        {generatedReferralCode.code}{' '}
                        <CopyToClipboard
                            textToCopy={generatedReferralCode.code}
                            textType='Referral Code'
                            anchorId='referral-code'
                        />
                    </span>
                )
            }
            // miles >= 10k and referral code does not exist. need to generate one
            else {
                return (
                    <span className='cursor-pointer text-[#85C3F8]' onClick={handleGenerateReferralCode}>
                        {isGenerating ? (
                            <span className='flex items-center gap-1'>
                                Generating <LoadingIcon className='h-5 w-5 animate-spin' />
                            </span>
                        ) : (
                            'Generate'
                        )}
                        {error && <div className='cursor-default text-[red]'>{error}</div>}
                    </span>
                )
            }
        }

        // miles < 10k and referral code does not exist
        return <span className='text-[red]'>Available after {MIN_MILES_FOR_REFERRAL_CODE.toLocaleString()} miles</span>
    }

    return (
        <Card>
            <div className='space-y-4'>
                <div className='flex flex-col items-center justify-center rounded-lg bg-black/25 p-4'>
                    <h3 className='text-lg'>Your MILES Earned</h3>
                    <div className='py-4 text-center text-6xl font-medium'>
                        <CountUp end={points} duration={1.75} />
                    </div>

                    <div className='text-sm'>Connected Wallet: {truncateAddress(address)}</div>
                    <br />

                    <div className='flex items-center justify-between'>
                        <div className='flex gap-2.5'>
                            <p className='text-sm'>Your Referral Code: {renderReferralCode()}</p>
                        </div>
                    </div>
                </div>

                {!isBreakdownAnEmptyObject && (
                    <div className='rounded-lg bg-black/25 p-4'>
                        <h3 className='mb-4 text-center text-xl font-semibold'>Points Breakdown</h3>
                        <PointsBreakdownChart breakdown={breakdown} />
                    </div>
                )}
            </div>
        </Card>
    )
}
